import React from 'react';
import { motion } from 'framer-motion';
import { Coins, FileSignature, BadgeCheck, RefreshCw, Infinity as InfinityIcon, UserX, Ban, Shuffle, ArrowRight, ArrowDown } from 'lucide-react';
import { Reveal, Stagger, Item, SectionHeading } from './Reveal';
import { BOOKING_URL, CTA_LABEL } from '../data/constants';

const flow = [
  { icon: Coins, step: '1 Credit', title: 'Zusammenarbeit startet', text: 'Mit dem Vermittlungsvertrag erwirbst du einen Credit – zum Festpreis von 7.999 €.' },
  { icon: FileSignature, step: 'Zugeordnet', title: 'Position im Vertrag', text: 'Der Credit ist der im Vertrag festgelegten Position zugeordnet. Wir starten die Suche.' },
  { icon: BadgeCheck, step: 'Eingelöst', title: 'Position dauerhaft besetzt', text: 'Besteht der Kandidat die Probezeit, ist der Credit abgegolten. Fertig.' },
];

const cases = [
  { icon: Shuffle, text: 'Position wurde zwischenzeitlich anderweitig besetzt' },
  { icon: Ban, text: 'Position wird nicht mehr benötigt' },
  { icon: UserX, text: 'Kandidat geht in der Probezeit – Grund egal' },
];

const CreditSection = () => (
  <section id="credits" className="relative bg-cream py-24 lg:py-32 overflow-hidden" data-testid="credit-section">
    <div className="absolute -left-40 top-20 w-[600px] h-[600px] rounded-full bg-amber-100/70 blur-[140px] pointer-events-none" />
    <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
      <SectionHeading
        eyebrow="Das Credit-System"
        title={<>Ein Credit. <span className="text-violet-gradient">Gültig, bis deine Position besetzt ist.</span></>}
        subtitle="Du zahlst nicht für Versuche, sondern für ein Ergebnis. Dein Credit verfällt nie – egal, was zwischendurch passiert."
      />

      <Stagger className="mt-16 grid md:grid-cols-3 gap-5 relative" stagger={0.12}>
        <div className="hidden md:block absolute top-14 left-[18%] right-[18%] h-px bg-gradient-to-r from-violet-400 via-amber-400 to-emerald-400" />
        {flow.map((f, i) => (
          <Item key={i} className="lux-card p-7 relative" data-testid={`credit-flow-${i}`}>
            <div className="flex items-center justify-between">
              <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg ${i === 2 ? 'bg-emerald-500 shadow-emerald-500/30' : 'bg-obsidian shadow-violet-900/30'}`}>
                <f.icon className={`w-6 h-6 ${i === 2 ? 'text-white' : 'text-amber-400'}`} />
              </div>
              <span className="text-xs font-bold uppercase tracking-[0.18em] text-violet-700 bg-violet-50 border border-violet-100 px-3 py-1 rounded-full">{f.step}</span>
            </div>
            <h3 className="mt-6 text-xl font-bold text-slate-900">{f.title}</h3>
            <p className="mt-2 text-slate-600 text-sm md:text-base leading-relaxed">{f.text}</p>
            {i < 2 && <ArrowRight className="hidden md:block absolute top-12 -right-5 w-5 h-5 text-amber-500 z-10" />}
          </Item>
        ))}
      </Stagger>

      <Reveal delay={0.2} className="mt-6 flex justify-center">
        <div className="flex flex-col items-center text-slate-400 text-xs font-semibold uppercase tracking-[0.2em]">
          <span>Und falls es anders kommt?</span>
          <ArrowDown className="w-4 h-4 mt-2 text-amber-500 animate-bounce" />
        </div>
      </Reveal>

      <Reveal delay={0.25} className="mt-6 relative" data-testid="credit-fallback-card">
        <div className="absolute -inset-1 bg-gradient-to-r from-amber-400/30 via-violet-500/30 to-amber-400/30 rounded-[1.7rem] blur-lg opacity-70" />
        <div className="relative lux-card-dark p-8 lg:p-10 text-white grid lg:grid-cols-12 gap-10 items-center overflow-hidden">
          <div className="absolute -right-20 -top-20 w-72 h-72 rounded-full bg-violet-600/30 blur-3xl" />
          <div className="lg:col-span-5 relative">
            <p className="eyebrow text-amber-300"><span className="eyebrow-line" />Dein Credit bleibt</p>
            <h3 className="mt-5 text-2xl md:text-3xl font-extrabold leading-tight">In diesen Fällen behältst du deinen Credit vollständig:</h3>
            <ul className="mt-6 space-y-3">
              {cases.map((c, i) => (
                <li key={i} className="flex items-center gap-3 text-slate-300 text-sm md:text-base" data-testid={`credit-case-${i}`}>
                  <span className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center shrink-0"><c.icon className="w-4 h-4 text-amber-400" /></span>
                  {c.text}
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-7 relative">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="glass-dark rounded-2xl p-6">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 12, repeat: Infinity, ease: 'linear' }}
                  className="w-12 h-12 rounded-full bg-amber-400/15 border border-amber-400/30 flex items-center justify-center"
                >
                  <RefreshCw className="w-5 h-5 text-amber-400" />
                </motion.div>
                <p className="mt-4 font-bold text-lg">Gleiche oder völlig andere Vakanz</p>
                <p className="mt-1.5 text-sm text-slate-400 leading-relaxed">Setz den Credit erneut für dieselbe Stelle ein – oder für eine komplett andere Position in deinem Unternehmen.</p>
              </div>
              <div className="glass-dark rounded-2xl p-6 border-amber-400/20">
                <div className="w-12 h-12 rounded-full bg-amber-400/15 border border-amber-400/30 flex items-center justify-center">
                  <InfinityIcon className="w-6 h-6 text-amber-400" />
                </div>
                <p className="mt-4 font-bold text-lg">Unbegrenzt gültig</p>
                <p className="mt-1.5 text-sm text-slate-400 leading-relaxed">Kein Ablaufdatum. Dein Credit gilt, bis eine Position dauerhaft besetzt ist. Erst dann ist er eingelöst.</p>
              </div>
            </div>
            <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4 rounded-2xl bg-white/5 border border-white/10 px-5 py-4">
              <p className="text-sm text-slate-300"><span className="text-gold font-bold text-base">7.999 €</span> · ein Credit · ein Ergebnis</p>
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="btn-gold px-5 py-2.5 text-sm w-full sm:w-auto" data-testid="credit-cta">
                {CTA_LABEL} <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

export default CreditSection;
